import { useReducer, useEffect, useCallback, useState, useRef } from "react";
import { withFestivalExperience } from "@/lib/festivalExperience";
import { toast } from "react-hot-toast";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { GETREQUESTAPI, POSTREQUESTAPI } from "@/pages/api/apiUtils";
import { fetchAllLanguages, fetchAllLocations } from "@/pages/api";
import Pagination from "@/components/pagination/Pagination";
import PoojaImageComponent from "@/components/PoojaComponent/PoojaImageBoxComponent";
import FilterBar from "@/components/v2/FilterBar";
import ActiveFilters from "@/components/v2/ActiveFilters";
import FestivalCollection from "@/components/festival/FestivalCollection";
import {
  BACKEND_API_ROUTE,
  GET_POOJA_ITEMS,
  POOJA_WISHLIST_API,
  POOJA_CART_API,
} from "@/commons/api_routes";
import { createPoojaQuery, getCookieItem } from "@/utils/utils";
import Shimmer from "@/components/AnimationComponent/Shimmer.jsx";
import { findLocLangIds } from "@/utils/locLangUtils";
import { useV2GlobalData } from "@/hooks/useV2GlobalData";
import StickyMobileContact from "@/components/v2/StickyMobileContact";
import Head from "next/head";


const initialState = {
  poojaItems: [],
  loading: true,
  currentPage: 1,
  recordsPerPage: 12,
  totalItems: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_POOJA_ITEMS":
      return { ...state, poojaItems: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_TOTAL_ITEMS":
      return { ...state, totalItems: action.payload };
    default:
      return state;
  }
}

// Mirrors how the client reads filters from the URL, so the server fetches
// exactly what the first client render would have asked for.
const parseFiltersFromQuery = (query = {}) => {
  const hasPrice = query.minPrice || query.maxPrice;
  return {
    categories: query.categories ? String(query.categories).split(",") : [],
    priceRange: hasPrice
      ? [
          query.minPrice ? parseInt(query.minPrice) : 3500,
          query.maxPrice ? parseInt(query.maxPrice) : 21000,
        ]
      : [501, 110000],
    durations: query.durations ? String(query.durations).split(",") : [],
    sortBy: query.sort ? String(query.sort) : "popular",
    search: query.q ? String(query.q) : "",
    currentPage: query.page ? parseInt(query.page) : 1,
  };
};

// Shared by getServerSideProps and the client fetch. The URL doubles as the
// cache key that lets the client skip re-fetching what the server rendered.
const buildPoojaListUrl = ({ languageId, locationId, search, currentPage, recordsPerPage, sortBy, priceRange, categories }) => {
  if (search && search.trim() !== "") {
    return `${BACKEND_API_ROUTE}/pooja/find?t=${encodeURIComponent(search.trim())}&language=${languageId}&location=${locationId}&p=false`;
  }

  const queryParams = new URLSearchParams({
    page: currentPage - 1,
    pageSize: recordsPerPage,
    sort: sortBy.replace("-", "_"),
    projection: "PLP",
  });

  // Add price min/max filters
  queryParams.append("minPrice", priceRange[0]);
  queryParams.append("maxPrice", priceRange[1]);

  // Add categories
  categories.forEach(cat => {
    queryParams.append("category", cat);
  });
  queryParams.append("language", languageId);
  queryParams.append("location", locationId);
  return `${BACKEND_API_ROUTE}${GET_POOJA_ITEMS}?${queryParams}`;
};

const toDisplayName = (segment) =>
  String(segment || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/^E Pooja$/, "E-Pooja");

const PoojaPage = ({ seo, initialPoojaList, initialLanguageId, initialLocationId }) => {
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialPoojaList, (ssr) =>
    ssr
      ? {
          ...initialState,
          poojaItems: ssr.items,
          totalItems: ssr.totalCount,
          currentPage: ssr.currentPage,
          loading: false,
        }
      : initialState
  );
  // URL of the last list we have data for; starts as the server-rendered one
  const lastFetchedUrlRef = useRef(initialPoojaList?.url || null);
  const { category, language: routerLanguage, location: routerLocation } = router.query;
  const { locations, languages } = useSelector((state) => state.locationandlanguages || {});

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([501, 110000]);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [sortBy, setSortBy] = useState("popular");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isUrlInitialized, setIsUrlInitialized] = useState(false);

  const { data: v2Data } = useV2GlobalData();
  const categoriesList = v2Data?.generalCategories || [];

  // Initialize state from URL
  useEffect(() => {
    if (router.isReady && !isUrlInitialized) {
      let pageChanged = false;
      if (router.query.categories) setSelectedCategories(router.query.categories.split(','));
      if (router.query.minPrice || router.query.maxPrice) {
        setPriceRange([
          router.query.minPrice ? parseInt(router.query.minPrice) : 3500,
          router.query.maxPrice ? parseInt(router.query.maxPrice) : 21000
        ]);
      }
      if (router.query.durations) setSelectedDurations(router.query.durations.split(','));
      if (router.query.sort) setSortBy(router.query.sort);
      if (router.query.q) {
        setSearch(router.query.q);
        setDebouncedSearch(router.query.q);
      }
      if (router.query.page) {
        dispatch({ type: "SET_CURRENT_PAGE", payload: parseInt(router.query.page) });
        pageChanged = true;
      }
      setIsUrlInitialized(true);
    }
  }, [router.isReady, router.query, isUrlInitialized]);

  // Sync state to URL
  useEffect(() => {
    if (!isUrlInitialized) return;

    const query = { ...router.query };
    
    if (selectedCategories.length) query.categories = selectedCategories.join(",");
    else delete query.categories;

    if (priceRange[0] !== 3500 || priceRange[1] !== 21000) {
      query.minPrice = priceRange[0];
      query.maxPrice = priceRange[1];
    } else {
      delete query.minPrice;
      delete query.maxPrice;
    }

    if (selectedDurations.length) query.durations = selectedDurations.join(",");
    else delete query.durations;

    if (sortBy && sortBy !== "popular") query.sort = sortBy;
    else delete query.sort;

    if (search) query.q = search;
    else delete query.q;

    if (state.currentPage > 1) query.page = state.currentPage;
    else delete query.page;

    router.push({
      pathname: router.pathname,
      query: query
    }, undefined, { shallow: true });

  }, [selectedCategories, priceRange, selectedDurations, sortBy, search, state.currentPage, isUrlInitialized]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const handleCategoryToggle = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleDurationToggle = (dur) => {
    setSelectedDurations(prev => prev.includes(dur) ? prev.filter(d => d !== dur) : [...prev, dur]);
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleClearAll = () => {
    setSelectedCategories([]);
    setSelectedDurations([]);
    setPriceRange([3500, 21000]);
    setSearch("");
    setDebouncedSearch("");
    setSortBy("popular");
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleSearchChange = (val) => {
    if (search !== val) {
      setSearch(val);
      if (val === "") {
        setDebouncedSearch("");
      }
      dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
    }
  };
  const handlePriceChange = (val) => {
    setPriceRange(val);
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
  };
  const handleSortChange = (val) => {
    if (sortBy !== val) {
      setSortBy(val);
      dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
    }
  };

  const { currentPage, recordsPerPage, poojaItems, totalItems, loading } = state;
  const nPages = Math.ceil(totalItems / recordsPerPage);

  // Find location and language IDs based on router query
  // Falls back to the server-resolved IDs while the Redux lists are still loading
  const findLocationAndLanguageIds = useCallback(() => {
    const ids = findLocLangIds(locations, languages, routerLocation, routerLanguage);
    return {
      locationId: ids.locationId || initialLocationId || null,
      languageId: ids.languageId || initialLanguageId || null,
    };
  }, [routerLocation, routerLanguage, locations, languages, initialLocationId, initialLanguageId]);

  const postUserCartApi = async (data) => {
    try {
      const { locationId, languageId } = findLocationAndLanguageIds()
      if (!locationId || !languageId) {
        toast.error("Location or language not found");
        return;
      }

      const cartApiUrl = BACKEND_API_ROUTE + POOJA_CART_API;
      const accessToken = getCookieItem("accessToken");
      const payload = {
        poojaId: data._id,
        language: languageId,
        location: locationId,
      };

      const cartResponse = await POSTREQUESTAPI(cartApiUrl, payload, accessToken);

      if (cartResponse.status === 201 && cartResponse.data.statusCode === 200) {
        toast.success(cartResponse.data.message);
      } else {
        toast.error(cartResponse.data.error || "Failed to add item to cart");
      }
    } catch (error) {
      toast.error("Error adding to cart. Please try again.");
    }
  };

  const postUserWishlistApi = async (data) => {
    try {
      const wishlistApiUrl = BACKEND_API_ROUTE + POOJA_WISHLIST_API;
      const accessToken = getCookieItem("accessToken");

      if (!accessToken) {
        toast("Please login to add item to wishlist");
        return;
      }

      const payload = {
        poojaId: data._id,
      };

      const wishlistResponse = await POSTREQUESTAPI(
        wishlistApiUrl,
        payload,
        accessToken
      );

      if (wishlistResponse.status === 201 && wishlistResponse.data.statusCode === 200) {
        toast.success(wishlistResponse.data.message);
      } else {
        toast.error(wishlistResponse.data.error || "Failed to add item to wishlist");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast("Please login to add item to wishlist");
      } else {
        toast.error("Error adding to wishlist. Please try again.");
      }
    }
  };

  const fetchPoojaItems = useCallback(async () => {
    const { locationId, languageId } = findLocationAndLanguageIds();
    if (!locationId || !languageId) {
      dispatch({ type: "SET_LOADING", payload: false });
      dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
      return;
    }

    const url = buildPoojaListUrl({
      languageId,
      locationId,
      search: debouncedSearch,
      currentPage,
      recordsPerPage,
      sortBy,
      priceRange,
      categories: selectedCategories,
    });

    // Already rendered (by the server, or by an earlier identical fetch)
    if (url === lastFetchedUrlRef.current) return;
    lastFetchedUrlRef.current = url;

    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const response = await GETREQUESTAPI(url);

      if (response.status === 200) {
        const data = response.data;
        if (data.statusCode === 200) {
          dispatch({ type: "SET_POOJA_ITEMS", payload: data.body });
          dispatch({ type: "SET_TOTAL_ITEMS", payload: data.totalCount });
        } else {
          toast.error("No Matching Pooja Found!");
          dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
        }
      } else {
        toast.error("Something Went Wrong!");
        dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
      }
    } catch (error) {
      lastFetchedUrlRef.current = null;
      toast.error("Error fetching pooja items. Please try again.");
      dispatch({ type: "SET_POOJA_ITEMS", payload: [] });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [currentPage, recordsPerPage, findLocationAndLanguageIds, debouncedSearch, priceRange, selectedCategories, sortBy]);

  useEffect(() => {
    if (router.isReady && isUrlInitialized) {
      fetchPoojaItems();
    }
  }, [router.isReady, isUrlInitialized, fetchPoojaItems]);

  const handlePageChange = (pageNumber) => {
    dispatch({ type: "SET_CURRENT_PAGE", payload: pageNumber });
  };

  const handlePoojaItemClick = (poojaData) => {
    const poojaPath = `/${routerLanguage?.toLowerCase()}/${routerLocation?.toLowerCase()}/pooja/${poojaData.slug}`;
    router.push(poojaPath);
  };

  const renderBreadcrumbs = () => {
    const breadcrumbs = [];

    if (routerLocation) {
      breadcrumbs.push(routerLocation.replace(/-/g, " "));
    }

    if (routerLanguage) {
      breadcrumbs.push(routerLanguage.replace(/-/g, " "));
    }

    if (category) {
      breadcrumbs.push(category.replace(/-/g, " "));
    }

    breadcrumbs.push("Pooja Services");

    return breadcrumbs.join(" / ");
  };

  return (
    <div>
      <Head>
        <title>{seo.title}</title>
        {seo.noIndex && <meta name="robots" content="noindex, follow" />}
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
      </Head>
      <div className="bg-background min-h-screen">
        {/* Breadcrumb */}
        <div className="container mx-auto px-4 pt-4 pb-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            {renderBreadcrumbs()}
          </p>
        </div>

        {/* Page Title with result count */}
        <div className="container mx-auto px-4 pb-2">
          <h1 className="text-xl md:text-2xl font-bold text-foreground capitalize">
            {routerLanguage?.replace(/-/g, " ")} Pooja Services in {routerLocation?.replace(/-/g, " ")}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Showing {totalItems} sacred rituals available
          </p>
        </div>

        {/* Filter Bar */}
        <div className="container mx-auto px-4">
          <FilterBar
            categories={categoriesList}
            selectedCategories={selectedCategories}
            onCategoryToggle={handleCategoryToggle}
            priceRange={priceRange}
            onPriceChange={handlePriceChange}
            selectedDurations={selectedDurations}
            onDurationToggle={handleDurationToggle}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            search={search}
            onSearchChange={handleSearchChange}
            onClearAll={handleClearAll}
          />
        </div>

        {/* Active filter chips */}
        <div className="container mx-auto px-4 py-2">
          <ActiveFilters
            search={search}
            onClearSearch={() => handleSearchChange("")}
            categories={selectedCategories}
            onRemoveCategory={handleCategoryToggle}
            durations={selectedDurations}
            onRemoveDuration={handleDurationToggle}
            priceRange={priceRange}
            onResetPrice={() => handlePriceChange([3500, 21000])}
            onClearAll={handleClearAll}
          />
        </div>

        {/* Festival Experience — contextual collection (renders nothing when no campaign) */}
        <div className="container mx-auto px-4 pt-2">
          <FestivalCollection slotKey="listing.collection" />
        </div>

        <section className="px-4 py-4 container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 2xl:gap-6">
            {loading ? (
              Array.from({ length: 12 }, (_, index) => (
                <Shimmer key={`shimmer-${index}`} />
              ))
            ) : poojaItems.length > 0 ? (
              poojaItems.map((boxData, index) => (
                <div
                  key={`pooja-item-${boxData._id || index}`}
                  className="max-w-[500px]"
                >
                  <PoojaImageComponent
                    imageUrl={boxData.media?.[0]?.mediaUrl}
                    altText={boxData.name}
                    poojaData={boxData}
                    postUserWishlistApi={postUserWishlistApi}
                    handlePoojaItemClick={handlePoojaItemClick}
                    postUserCartApi={postUserCartApi}
                  />
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-10">
                No pooja items found for the selected criteria
              </div>
            )}
          </div>

          {poojaItems.length > 0 && nPages > 1 && (
            <div className="flex flex-row justify-end items-center font-bastoni text-4xl text-Default mt-6">
              <Pagination
                nPages={nPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </section>
      </div>
      <StickyMobileContact />
    </div>
  );
};

// Server-rendered so the <title>/description carry the real language and city
// (statically optimised, the HTML was built with an empty route and no title).
async function getPageServerSideProps({ params, query }) {
  const language = String(params.language || "").toLowerCase();
  const location = String(params.location || "").toLowerCase().replace(/\s+|%20/g, "-");
  const languageName = toDisplayName(language);
  const locationName = toDisplayName(location);
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://bookmypoojaonline.com";
  const isOnline = location === "e-pooja";

  const seo = isOnline
    ? {
        title: `Online E-Pooja with ${languageName} Pandit | Book Puja Online | BookMyPooja`,
        description: `Book online e-pujas performed by verified ${languageName}-speaking pandits, with live video participation from anywhere. Transparent package prices with dakshina included.`,
      }
    : {
        title: `Book ${languageName} Pandit in ${locationName} for Pooja & Homa | BookMyPooja`,
        description: `Book verified ${languageName}-speaking pandits in ${locationName} for pujas, homas, japas and ceremonies at home. Basic, Standard and Premium packages with puja samagri and dakshina included.`,
      };

  // Render the pooja list into the HTML so crawlers and link previews see it.
  // On any failure we return no list and the client fetches it as before.
  let initialPoojaList = null;
  let initialLanguageId = null;
  let initialLocationId = null;
  try {
    const [languagesRes, locationsRes] = await Promise.all([
      fetchAllLanguages(),
      fetchAllLocations(),
    ]);
    const { languageId, locationId } = findLocLangIds(
      locationsRes?.data,
      languagesRes?.data,
      params.location,
      params.language
    );
    initialLanguageId = languageId;
    initialLocationId = locationId;

    // Unknown language/city slug → real 404 rather than an empty, indexable listing
    const listsOk = languagesRes?.data?.statusCode === 200 && locationsRes?.data?.statusCode === 200;
    if (listsOk && (!languageId || !locationId)) {
      return { notFound: true };
    }

    if (languageId && locationId) {
      const filters = parseFiltersFromQuery(query);
      const url = buildPoojaListUrl({
        languageId,
        locationId,
        search: filters.search,
        currentPage: filters.currentPage,
        recordsPerPage: initialState.recordsPerPage,
        sortBy: filters.sortBy,
        priceRange: filters.priceRange,
        categories: filters.categories,
      });
      const response = await GETREQUESTAPI(url);
      if (response.status === 200 && response.data?.statusCode === 200) {
        initialPoojaList = {
          url,
          items: response.data.body || [],
          totalCount: response.data.totalCount || 0,
          currentPage: filters.currentPage,
        };
      }
    }
  } catch (error) {
    console.error("Error fetching pooja list in getServerSideProps:", error.message);
  }

  // Keep confirmed-empty listings out of Google ("No pooja items found" snippets / sitelinks).
  // A failed fetch (null list) stays indexable, same as category pages.
  const noIndex = initialPoojaList?.totalCount === 0;

  return {
    props: {
      seo: { ...seo, canonical: `${siteUrl}/${language}/${location}/pooja`, noIndex },
      initialPoojaList,
      initialLanguageId,
      initialLocationId,
    },
  };
}

export default PoojaPage;

// Festival layer resolved alongside the page data (renders nothing if no campaign).
export const getServerSideProps = withFestivalExperience("listing", getPageServerSideProps);
